// export default ThreeDScene;
import { Canvas, useThree } from "@react-three/fiber";
import { useGLTF, Stage, OrbitControls, Edges, useProgress } from "@react-three/drei";
import { Box, Card, CardContent, Typography, Button, TextField, MenuItem, FormGroup, FormControlLabel, Checkbox, Tabs, Tab, Select, FormControl, Slider } from '@mui/material';
import { useEffect, useState, useMemo, useRef } from "react";
import * as THREE from 'three';
import { SketchPicker } from 'react-color';
import Draggable from 'react-draggable';
import axios from 'axios';
import { useSelector } from "react-redux";
import ReactApexChart from 'react-apexcharts';
import { Cube, CubeOutline, GridOn, ViewModule, ViewComfy } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ReactECharts from 'echarts-for-react';
import { unselectableComponents, lightThemeStyles, darkThemeStyles, voltageData, currentData, componentInfoDefaults, temperatureTrendSeries,gaugeOptions } from './constants';
import { fetchData,fetchMappings, createMapping } from "../../Restclient/datacollection";


// Register the necessary components
/**
 * A list of components we'll consider "unselectable."
 */

// Example: node name -> assigned layer
const layerAssignments = {
  _1f3: 'layerA',
  _1f1: 'layerB',
  _2f2: 'layerA',
  _2f4: 'layerC',
  node5: 'layerB',
};
const RadialChart = ({ label, value }) => {
  const [chartSize, setChartSize] = useState({ height: 117, width: 74 }); // Initial size

  useEffect(() => {
    const handleResize = () => {
      const newHeight = 175 * window.innerWidth / 1920; // Adjust based on screen width
      const newWidth = 110* window.innerWidth / 1920; // Adjust based on screen width
      setChartSize({ height: newHeight, width: newWidth });
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const lightercolor = label === "R Pole" ? "#DC272D" : label === "Y Pole" ? "#FFD000" : "#008CD7";
  const color = "#C1C1C1";

  const options = {
    chart: {
      height: chartSize.height,
      width: chartSize.width,
      type: 'radialBar',
    },
    plotOptions: {
      radialBar: {
        hollow: {
          size: '50%',
        },
        track: {
          background: color,
        },
        dataLabels: {
          name: {
            fontSize: '16px',
            fontWeight: 'bold',
            fontColor: lightercolor,
            color: lightercolor,
          },
          value: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: lightercolor,
          },
        },
      },
    },
    colors: [lightercolor],
    labels: [label]
  };

  return (
    <ReactApexChart
      options={options}
      series={[value]}
      type="radialBar"
      height={chartSize.height}
      width={chartSize.width}
    />
  );
};

const temperatureTrendOptions = {
  chart: {
    type: 'line',
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  stroke: {
    curve: 'smooth',
    width: 1.5,
  },
  grid: {
    show: true,
    borderColor: 'var(--grey-variation)',
  },
  xaxis: {
    type: 'datetime',
    labels: {
      show: true,
      style: {
        colors: 'var(--axis-color)',
        fontSize: '12px',
        fontFamily: 'Roboto',
        fontWeight: 800,
      },
      formatter: function(value, timestamp, opts) {
        if (isNaN(value)) return '';
        const currentFormattedDate = new Date(value);
        return currentFormattedDate.toLocaleDateString('en-us', { month: "short", day: "numeric" });
      },
    },
    axisBorder: {
      show: true,
      color: 'var(--axis-color)',
    },
    axisTicks: {
      show: true,
      height: 3,
      color: 'var(--axis-color)',
    },
  },
  yaxis: {
    title: {
      text: 'Temperature (°C)',
      style: {
        color: 'var(--axis-title-color)',
        fontSize: '12px',
        fontFamily: 'Roboto',
        fontWeight: 800,
      },
    },
    labels: {
      style: {
        colors: 'var(--axis-color)',
        fontSize: '12px',
        fontFamily: 'Roboto',
        fontWeight: 800,
      },
      formatter: (value) => value?.toFixed(2),
    },
  },
  markers: {
    radius: 12,
    height: "10px",
    width: "10px"
    
  },
  tooltip: {
    enabled: true,
    custom: function({ series, seriesIndex, dataPointIndex, w }) {
      
      console.log("series in chart",series)
      console.log("series in chart-dataPointIndex",dataPointIndex,"seriesIndex",seriesIndex)
      const value = series[seriesIndex][dataPointIndex];
      const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex];
      const currentFormattedDate = new Date(timestamp);
      const formattedDate = currentFormattedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g, '$3-$1-$2');
      
      let tooltipString = `<div style="display:flex; flex-direction:column; padding:5px;">`;
      tooltipString += `<div style="font-weight:600; color:#FFFFFF;">${formattedDate}</div>`;
      for (let i = 0; i < series.length; i++) {
        const seriesValue = series[i][dataPointIndex];
        tooltipString += `
          <div style="display:flex; align-items:center; margin-bottom:4px;">
            <div style="height:8px; width:8px; background-color:${w.config.colors[i]}; border-radius:50%; margin-right:8px;"></div>
            <div style="font-weight:600; color:${w.config.colors[i]};">${w.config.series[i].name}</div>
            <div style="margin-left:12px; color:${w.config.colors[i]};">${seriesValue?.toFixed(2)}</div>
          </div>
        `;
      }
      tooltipString += `</div>`;
      return tooltipString;
    },
  },
  colors: ['#FF5733', '#FFD000', '#008CD7'],
  states: {
    hover: {
      filter: {
        type: 'none',
      },
    },
  },
};

/**
 * Recursively build a scene graph preserving each node's transform.
 * We attach pointer events and dynamic materials to any mesh children.
 */
function buildSceneGraph({
  object,
  materials,
  hovered,
  selected,
  onPointerOver,
  onPointerOut,
  onPointerDown,
  visibleLayers
}) {
  // Determine if the object is visible based on its assigned layer
  let isVisible = true;

  if (object.isMesh) {
    const nodeName = object.name;
    // If node not in layerAssignments, default it to 'default' layer
    const nodeLayer = layerAssignments[nodeName] || 'default';
    isVisible = visibleLayers.includes(nodeLayer);
  }

  if (object.isMesh) {
    const nodeName = object.name;

    // ----------------------------------
    // ADDED: compute rotation based on selection and nodeName check
    // original glTF rotation:
    const originalRot = [object.rotation.x, object.rotation.y, object.rotation.z];
    // rotate 90 degrees if it's a "VOLTMETER_SELECTOR_SWITCH" and selected
    const finalRotation =
      nodeName.includes("VOLTMETER_SELECTOR_SWITCH") && selected.includes(nodeName)
        ? [originalRot[0], originalRot[1] + Math.PI / 2, originalRot[2]]
        : originalRot;
    // ----------------------------------

    return (
      <group
        key={nodeName}
        position={object.position}
        // use the finalRotation we computed
        rotation={finalRotation}
        scale={object.scale}
        visible={isVisible} // hide this mesh if its layer is not selected
      >
        <mesh
          geometry={object.geometry}
          material={materials[nodeName] || object.material}
          onPointerOver={(event) => onPointerOver(event, nodeName)}
          onPointerOut={(event) => onPointerOut(event)}
          onPointerDown={(event) => onPointerDown(event, nodeName)}
        />
        
        {/* Always render a thin boundary (1px) */}
        <Edges
          geometry={object.geometry}
          color="#000000"
          lineWidth={1}
        />

        {/* Render the thicker edge only when selected or hovered */}
        {(selected.includes(nodeName) || hovered === nodeName) && (
          <Edges
            geometry={object.geometry}
            color={selected.includes(nodeName) ? "white" : "#4F86F7"}
            lineWidth={2}
          />
        )}
      </group>
    );
  }

  // If object has children, recurse
  return (
    <group
      key={object.uuid}
      position={object.position}
      rotation={object.rotation}
      scale={object.scale}
    >
      {object.children.map((child) =>
        buildSceneGraph({
          object: child,
          materials,
          hovered,
          selected,
          onPointerOver,
          onPointerOut,
          onPointerDown,
          visibleLayers
        })
      )}
    </group>
  );
}

/**
 * The main 3D Model component.
 * 
 * IMPORTANT: We take the color from the GLB file's material instead of manually overriding.
 */
function Model({ onSelect, viewMode, mode, selectedComponent, visibleLayers, mappings }) {
  const { scene } = useGLTF("/model_final.glb");
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState([]);

  // Cache the scene using useMemo
  const cachedScene = useMemo(() => {
    return scene.clone();
  }, [scene]);

  useEffect(() => {
    if (!selectedComponent) {
      setSelected([]);
    }
  }, [selectedComponent]);

  const handlePointerOver = (event, nodeName) => {
    event.stopPropagation();
    setHovered(nodeName);
  };

  const handlePointerOut = (event) => {
    event.stopPropagation();
    setHovered(null);
  };

  const handlePointerDown = (event, nodeName) => {
    event.stopPropagation();
    if (unselectableComponents.some((unselectable) => nodeName.includes(unselectable))) {
      return;
    }
    if (mode === 'view') {
      const dataName = mappings[nodeName];
      const componentsToSelect = Object.keys(mappings).filter(
        (component) => mappings[component] === dataName
      );
      setSelected(componentsToSelect);
    } else if (mode === 'build') {
      setSelected((prevSelected) => {
        if (prevSelected.includes(nodeName)) {
          return prevSelected.filter((name) => name !== nodeName);
        } else {
          return [...prevSelected, nodeName];
        }
      });
    }
    onSelect(nodeName);
  };

  const materials = useMemo(() => {
    const matDict = {};
    cachedScene.traverse((child) => {
      if (child.isMesh) {
        const nodeName = child.name;
        const originalMat = child.material.clone();
        if (nodeName.includes("Solid037")) {
          originalMat.color = new THREE.Color('dodgerblue');
        } else if (selected.includes(nodeName)) {
          originalMat.color = new THREE.Color('dodgerblue');
          originalMat.transparent = true;
          originalMat.opacity = 0.5;
        } else if (viewMode === 'translucent') {
          originalMat.transparent = true;
          originalMat.opacity = 0.5;
        } else {
          originalMat.transparent = false;
          originalMat.opacity = 1.0;
        }
        originalMat.wireframe = (viewMode === 'wireframe');
        matDict[nodeName] = originalMat;
      }
    });
    return matDict;
  }, [cachedScene, selected, viewMode]);

  // Memoize the scene graph to prevent unnecessary re-renders
  const sceneGraph = useMemo(() => {
    return buildSceneGraph({
      object: cachedScene, // Use cachedScene instead of scene
      materials,
      hovered,
      selected,
      onPointerOver: handlePointerOver,
      onPointerOut: handlePointerOut,
      onPointerDown: handlePointerDown,
      visibleLayers
    });
  }, [cachedScene, materials, hovered, selected, visibleLayers]);

  return (
    <group dispose={null} scale={0.006}>
      {sceneGraph}
    </group>
  );
}

/**
 * Optional helper to enable zoom with mouse wheel
 */
function ZoomControls() {
  const { camera } = useThree();

  useEffect(() => {
    const handleWheel = (event) => {
      event.preventDefault();
      camera.zoom += event.deltaY * -0.001;
      camera.zoom = Math.max(0.1, Math.min(10, camera.zoom));
      camera.updateProjectionMatrix();
    };

    window.addEventListener('wheel', handleWheel);
    return () => window.removeEventListener('wheel', handleWheel);
  }, [camera]);

  return null;
}

function LoadingIndicator() {
  const { active, progress } = useProgress();
  return active ? (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: 'white',
      zIndex: 10,
      textAlign: 'center'
    }}>
      <div>Loading...</div>
      <div>{Math.round(progress)}%</div>
    </div>
  ) : null;
}

function ComponentDetails({ selectedComponent, componentNames, mappings, componentData, Icurrent }) {
  const [tabValue, setTabValue] = useState(0);
  const [subTabValue, setSubTabValue] = useState(0);

  // Determine the current theme styles
  const themeStyles = useSelector((state) => state.themedata.theme === 'dark' ? darkThemeStyles : lightThemeStyles);
  const theme = useSelector((state) => state.themedata.theme);

  const handleTabChange = (event, newValue) => {
    // Allow free tab switching if '_1f1' or '_1f3' is selected
    if (mappings[selectedComponent] === '_1f1' || mappings[selectedComponent] === '_1f3') {
      setTabValue(newValue);
    } else {
      // For other components, keep the tab fixed at 1
      setTabValue(1);
    }
  };

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  const componentMapping = mappings[selectedComponent];
  const componentInfo = componentData[componentMapping];

  // Determine the title based on mapping
  const title = componentMapping ? componentMapping.substring(1) : (componentNames[selectedComponent] || selectedComponent);

  // Prevent scroll event from affecting the parent components
  const handleScroll = (event) => {
    event.stopPropagation();
  };

  const renderTemperatureData = () => {
    return (
      <Box sx={themeStyles.cardStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 1, textAlign: 'left', borderBottom: '1px solid #B0B0B0' }}>
          <Typography variant="subtitle1" sx={{ color: theme === 'dark' ? '#FFFFFF' : '#000000', textAlign: 'left', fontWeight: 'bold' }}>
            Temperature (°C)
          </Typography>
          <Typography variant="subtitle1" sx={{ color: theme === 'dark' ? '#FFFFFF' : '#000000', flex: 1, textAlign: 'right', fontWeight: 'bold' }}>
            Incomer
          </Typography>
          <Typography variant="subtitle1" sx={{ color: theme === 'dark' ? '#FFFFFF' : '#000000', flex: 1, textAlign: 'right', fontWeight: 'bold' }}>
            Load
          </Typography>
        </Box>

        {['R', 'Y', 'B'].map((phase, index) => {
          const incomerColor = phase === 'R' ? ('red') :
                               phase === 'Y' ? ('orange') :
                               (theme === 'dark' ? '#88B6FB' : '#033176');
          const loadColor = incomerColor; // Assuming the same color for Load as Incomer
          return (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', padding: 1 }}>
              <Typography variant="body2" sx={{ color: theme === 'dark' ? '#FFFFFF' : '#000000', fontWeight: 'bold', flex: 1, textAlign: 'left' }}>
                {phase}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: incomerColor, flex: 1, textAlign: 'right' }}>
                {componentInfo[`incomerSide${phase}`]?.data || '0.00'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: loadColor, flex: 1, textAlign: 'right' }}>
                {componentInfo[`loadSide${phase}`]?.data || '0.00'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const calculateChartHeight = (categories) => {
    const heightPerCategory = 50; // Adjust this value as needed
    return categories.length * heightPerCategory;
  };

  const renderMeteringData = () => {
    const currentSeries = [
      { 
        name: 'Current', 
        data: [componentInfo.ir?.data || 1600, componentInfo.iy?.data || 2000, componentInfo.ib?.data || 1740, componentInfo.in?.data || 0],
      }
    ];

    const voltageSeries = [
      { name: 'Voltage', data: [componentInfo.vry?.data || 480, componentInfo.vyb?.data || 460, componentInfo.vbr?.data || 275] }
    ];

    const currentOptions = {
      chart: {
        type: 'bar',
        height: calculateChartHeight(['R', 'Y', 'B', 'N']),
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      xaxis: {
        categories: ['R', 'Y', 'B', 'N'],
        labels: {
          show: false,
        },
        lines: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: theme === 'dark' ? ['#FFFFFF'] : ['#000000'],
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      colors: [
        function ({ value, dataPointIndex, w }) {
          const label = w.config.xaxis.categories[dataPointIndex];
          if (label === "R") {
            return "#DC272D"; // Red
          } else if (label === "Y") {
            return "#FFD000"; // Orange
          } else if (label === "B") {
            return "#008CD7"; // Blue
          } else {
            return "#C1C1C1"; // Default color
          }
        },
      ],
      states: {
        hover: {
          filter: {
            type: 'none', // Disable hover effects
          },
        },
      },
    };
    
    const voltageOptions = {
      chart: {
        type: 'bar',
        height: calculateChartHeight(['RY', 'YB', 'BR']),
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      xaxis: {
        categories: ['RY', 'YB', 'BR'],
        labels: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: theme === 'dark' ? ['#FFFFFF'] : ['#000000'],
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      colors: [
        function ({ value, dataPointIndex, w }) {
          const label = w.config.xaxis.categories[dataPointIndex];
          if (label === "RY") {
            return "#DC272D"; // Red
          } else if (label === "YB") {
            return "#FFD000"; // Orange
          } else if (label === "BR") {
            return "#008CD7"; // Blue
          } else {
            return "#C1C1C1"; // Default color
          }
        },
      ],
      states: {
        hover: {
          filter: {
            type: 'none', // Disable hover effects
          },
        },
      },
    };

    return (
      <Box sx={themeStyles.cardStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #B0B0B0' }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: theme === 'dark' ? '#FFFFFF' : '#000000',
            textAlign: 'left',
            fontWeight: 'bold',
          }}
          flex={1}
        >
          Current (A)
        </Typography>
        </Box>
        <ReactApexChart 
          backgroundColor={theme === 'dark' ? '#000000' : '#F5F5F5'} 
          options={currentOptions} 
          series={currentSeries} 
          type="bar" 
          height={calculateChartHeight(['R', 'Y', 'B', 'N'])} 
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #B0B0B0' }}>
          <Typography 
          sx={{ 
            color: theme === 'dark' ? '#FFFFFF' : '#000000', 
            marginTop: 2,
            textAlign: 'left',
            fontWeight: 'bold',
          }}
          flex={1}
        >
          Voltage (V)
        </Typography>
        </Box>
        <ReactApexChart 
          backgroundColor={theme === 'dark' ? '#000000' : '#F5F5F5'} 
          options={voltageOptions} 
          series={voltageSeries} 
          type="bar" 
          height={calculateChartHeight(['RY', 'YB', 'BR'])} 
        />
      </Box>
    );
  };

  const renderVoltageData = () => {
    return (
      <Box  sx={{...themeStyles.cardStyle,
        
        backgroundColor: 'transparent',
        borderRadius: 1}}>
        <Typography variant="subtitle1" sx={{ paddingLeft: 0, paddingRight: 1, color: theme === 'dark' ? 'white' : 'black', textAlign: 'left', marginBottom:0.5, fontWeight: 'bold' ,borderBottom: '1px solid #B0B0B0'}}>
          Voltage (V)
        </Typography>
        {voltageData.map((item) => (
          <Box
            key={item.label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 0,
              paddingBottom: 0,
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: theme === 'dark' ? 'white' : 'black',
                fontSize: '16px', 
              }}
            >
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ color: theme === 'dark' ? 'white' : 'black', fontWeight: 'bold' }}>
              {componentInfo[item.key]?.data || '0.00'}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderCurrentData = () => {
    return (
      <Box  sx={{...themeStyles.cardStyle,
        background: 'transparent',
        borderRadius: 1}}>
        <Typography variant="subtitle1" sx={{ paddingLeft: 0, paddingRight: 1, color: theme === 'dark' ? 'white' : 'black', textAlign: 'left', marginBottom:0.5, fontWeight: 'bold' ,borderBottom: '1px solid #B0B0B0'}}>
          Current (A)
        </Typography>
        {currentData.map((item) => (
          <Box
            key={item.label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 1,
              fontWeight: 'bold',
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: theme === 'dark' ? 'white' : 'black',
                fontSize: '16px', 
              }}
            >
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ color: theme === 'dark' ? 'white' : 'black', fontWeight: 'bold' }}>
              {componentInfo[item.key]?.data || '0.00'}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Card
      sx={{
        ...themeStyles.cardStyle,
        padding: 1,
        paddingRight: 4,
        maxWidth: 600,
        overflow: 'hidden',
        marginTop: 1,
        marginLeft: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '99%'
      }}
      onWheel={handleScroll}
    >
      <CardContent sx={{ width: '100%', ...themeStyles.cardStyle, height: '100%' }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ ...themeStyles.titleStyle, textAlign: 'left', textTransform: 'uppercase' }}
        >
          {title}
        </Typography>
        {componentMapping && componentInfo && (
          <>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ marginBottom: 2 }}>
              <Tab label="General" sx={{ ...themeStyles.subtitleStyle, textTransform: 'none', fontWeight: 'bold' }} />
              <Tab label="Sensor Data" sx={{ ...themeStyles.subtitleStyle, textTransform: 'none', fontWeight: 'bold' }} />
              {componentMapping === '_1f1' && (
                <Tab label="Simulation" sx={{ ...themeStyles.subtitleStyle, textTransform: 'none', fontWeight: 'bold' }} />
              )}
            </Tabs>
            <Box sx={{ overflowY: 'auto' }}>
              {tabValue === 0 && (
                <Box sx={themeStyles.cardStyle}>
                  <Typography variant="subtitle1" sx={themeStyles.subtitleStyle}>
                    {/* General information can be added here */}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={themeStyles.bodyTextStyle}>Iframe</Typography>
                    <Typography variant="body2" sx={themeStyles.bodyTextStyle}>{componentInfo.iframe?.data || 'N/A'}</Typography>
                    <Typography variant="body2" sx={themeStyles.bodyTextStyle}>Irated</Typography>
                    <Typography variant="body2" sx={themeStyles.bodyTextStyle}>{componentInfo.rating?.data || 'N/A'}</Typography>
                    <Typography variant="body2" sx={themeStyles.bodyTextStyle}>Poles</Typography>
                    <Typography variant="body2" sx={themeStyles.bodyTextStyle}>{componentInfo.poles?.data || 'N/A'}</Typography>
                  </Box>
                </Box>
              )}
              {tabValue === 1 && (
                <>
                  {componentMapping === '_1f1' && (
                    <>
                      <Box sx={{ ...themeStyles.cardStyle }} style={{height:'100%'}}>
                        <Typography sx={{ fontWeight: 'bold', marginBottom: 1, borderBottom: '1px solid #B0B0B0' }}>
                          Temperature Data:
                        </Typography>
                        <Box sx={{ backgroundColor: theme === 'dark' ? '#000000' : '#F5F5F5', marginTop: 0.5, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                          {['HBB R', 'HBB Y', 'HBB B', 'CT R', 'CT Y', 'CT B'].map((label, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                textAlign: 'left',
                                padding: 1,
                                border: '1px solid #505050',
                                backgroundColor: theme === 'dark' ? '#000000' : '#F5F5F5'
                              }}
                            >
                              <Typography variant="h6" sx={{ color: theme === 'dark' ? '#B0B0B0' : '#000000', marginBottom: 1, textAlign: 'left' }}>
                                {label}
                              </Typography>
                              <Typography variant="h5" sx={{ color: theme === 'dark' ? 'white' : 'black', textAlign: 'left' }}>
                                {componentInfo[`temperatureSensor${index + 1}`]?.data || '0.00'} °C
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            display: 'flex',
                            paddingTop:'20px',
                            fontWeight: 'bold',
                            color: theme === 'dark' ? '#ffffff' : '#000000',
                            textAlign: 'left',
                            borderBottom: '1px solid #B0B0B0',
                          }}
                        >
                          Temperature Trend:
                        </Typography>
                        <div style={{height:'5px'}}></div>
                        <ReactApexChart
                          options={temperatureTrendOptions}
                          series={temperatureTrendSeries}
                          type="line"
                          height={'50%'}
                          width={'100%'}
                        />
                      </Box>
                    </>
                  )}
                  {componentMapping === '_1f3' && (
                    <>
                      <Tabs 
                        value={subTabValue} 
                        onChange={handleSubTabChange} 
                        sx={{ justifyContent: 'space-between',alignItems: 'center', backgroundColor: theme === 'dark' ? '#303030' : '#D3D3D3', borderRadius: 25, minHeight: '25px',height:'30px'}}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                      >
                        <Tab 
                          label="Metering Data" 
                          disableRipple
                          sx={{
                            flex: 1,
                            textAlign: 'center',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: '25px',
                            margin: '0px',
                            minHeight: '25px',
                            height:'30px',
                            backgroundColor: subTabValue === 0 ? '#007bff' : theme === 'dark' ? '#303030' : '#D3D3D3',
                            color: theme === 'dark' ? 'white' : 'black',
                            '&.Mui-selected': {
                              backgroundColor: '#007bff', 
                              color: 'white',
                            },
                          }} 
                        />
                        <Tab 
                          label="Breaker Data" 
                          disableRipple
                          sx={{ 
                            flex: 1,
                            textAlign: 'center',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: '25px',
                            margin: '0px',
                            minHeight: '25px',
                            height: '30px',
                            backgroundColor: subTabValue === 1 ? '#007bff' : theme === 'dark' ? '#303030' : '#D3D3D3',
                            color: theme === 'dark' ? 'white' : 'black',
                            '&.Mui-selected': {
                              backgroundColor: '#007bff',
                              color: 'white',
                            },
                          }} 
                        />
                      </Tabs>
                      {subTabValue === 0 && (
                        <>
                          {renderMeteringData()}
                          {renderTemperatureData()}
                        </>
                      )}
                      {subTabValue === 1 && (
                        <Box sx={themeStyles.cardStyle}>
                          {/* Contact Wear Section */}
                          <div style={{height:'5px'}}></div>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #B0B0B0' }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                color: theme === 'dark' ? '#FFFFFF' : '#000000',
                                textAlign: 'left',
                                fontWeight: 'bold',
                              }}
                              flex={1}
                            >
                              Contact Wear (%)
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, marginTop: 2 ,gap: '3px'}}>
                            <RadialChart label="R Pole" value={componentInfo.contactWearR?.data || 10.00} />
                            <RadialChart label="Y Pole" value={componentInfo.contactWearY?.data || 5.10} />
                            <RadialChart label="B Pole" value={componentInfo.contactWearB?.data || 7.5} />
                          </Box>

                          {/* Fault Counter Section */}
                          <Box sx={{ marginTop: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between',marginBottom: 2, borderBottom: '1px solid #B0B0B0' }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: theme === 'dark' ? '#FFFFFF' : '#000000',
                                  textAlign: 'left',
                                  fontWeight: 'bold',
                                }}
                                flex={1}
                              >
                                Fault Counter
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">L:</Typography>
                                <Typography variant="body2">{componentInfo.faultCounterL?.data || 'N/A'}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">I:</Typography>
                                <Typography variant="body2">{componentInfo.faultCounterI?.data || 'N/A'}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">G:</Typography>
                                <Typography variant="body2">{componentInfo.faultCounterG?.data || 'N/A'}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">ETU:</Typography>
                                <Typography variant="body2">{componentInfo.faultCounterS?.data || 'N/A'}</Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Operations Section */}
                          <Box sx={{ marginTop: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between',marginBottom: 2, borderBottom: '1px solid #B0B0B0' }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: theme === 'dark' ? '#FFFFFF' : '#000000',
                                  textAlign: 'left',
                                  fontWeight: 'bold',
                                }}
                                flex={1}
                              >
                                Operations
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">On Operation:</Typography>
                                <Typography variant="body2">{componentInfo.onOperation?.data || 'N/A'}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">Off Operation:</Typography>
                                <Typography variant="body2">{componentInfo.offOperation?.data || 'N/A'}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                  {componentMapping === '_2f2' && (
                    <Box sx={themeStyles.cardStyle}>
                      {/* Thermal Status Indicator */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #B0B0B0' }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            color: theme === 'dark' ? '#FFFFFF' : '#000000',
                            textAlign: 'left',
                            fontWeight: 'bold',
                          }}
                          flex={1}
                        >
                          Status
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.5, marginTop: .5 }}>
                        <Typography variant="subtitle1" sx={{ color: theme === 'dark' ? 'white' : 'black', fontSize: '16px',  }}>
                          Thermal
                        </Typography>
                        <Box
                          sx={{
                            width: 50,
                            height: 25,
                            borderRadius: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: componentInfo.thermalStatus?.data === 0 ? 'red' : 'green',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        >
                          {componentInfo.thermalStatus?.data === 1 ? 'HIGH' : 'OK'}
                        </Box>
                      </Box>

                      {/* Trip Information Indicator */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.5, marginTop: .5 }}>
                        <Typography variant="subtitle1" sx={{ color: theme === 'dark' ? 'white' : 'black', fontSize: '16px',  }}>
                          Trip Information
                        </Typography>
                        <Box
                          sx={{
                            width: 50,
                            height: 25,
                            borderRadius: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: componentInfo.tripInfo?.data === 0 ? 'green' : 'red',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        >
                          {componentInfo.tripInfo?.data === 1 ? 'TRP' : 'OK'}
                        </Box>
                      </Box>

                      {/* Status Indicator */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.5, marginTop: .5 }}>
                        <Typography variant="subtitle1" sx={{ color: theme === 'dark' ? 'white' : 'black', fontSize: '16px',  }}>
                          Status
                        </Typography>
                        <Box
                          sx={{
                            width: 50,
                            maxwidth: 50,
                            height: 25,
                            borderRadius: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: componentInfo.status?.data === 0 ? 'red' : 'green',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        >
                          {componentInfo.status?.data === 1 ? 'ON' : 'OFF'}
                        </Box>
                      </Box>

                      {/* Render Voltage and Current Data */}
                      {renderVoltageData()}
                      {renderCurrentData()}
                    </Box>
                  )}
                  {componentMapping === '_2f4' && (
                    <Box sx={themeStyles.cardStyle}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2, borderBottom: '1px solid #B0B0B0' }}>
                        <Typography variant="subtitle1" sx={{ color: theme === 'dark' ? 'white' : 'black',fontSize: '14px', 
                              fontWeight: 'bold' }}>
                          VFD Status
                        </Typography>
                        <Box
                          sx={{
                            width: 50, // Adjust width for pill shape
                            height: 25, // Adjust height for pill shape
                            borderRadius: '15px', // Makes the box pill-shaped
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: componentInfo.vfdStatus?.data === 0 ? 'green' : componentInfo.vfdStatus?.data === 1 ? 'red' : componentInfo.vfdStatus?.data === 2 ? 'red' : 'green',
                            color: 'white', // Text color
                            fontWeight: 'bold', // Text weight
                          }}
                        >
                          {componentInfo.vfdStatus?.data === 2 ? 'TRP' : componentInfo.vfdStatus?.data === 1 ? 'ON' : 'OFF'}
                        </Box>
                      </Box>
                      {componentInfoDefaults.map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 1,
                            paddingBottom: 1,
                            borderBottom: '1px solid #B0B0B0',
                          }}
                        >
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              color: theme === 'dark' ? 'white' : 'black',
                              fontSize: '14px', 
                              fontWeight: 'bold' 
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: theme === 'dark' ? 'white' : 'black' }}>
                            {componentInfo[item.key]?.data || item.defaultValue}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
              {tabValue === 2 && componentMapping === '_1f1' && (
                <Box sx={themeStyles.cardStyle}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', gap: 2 }}>
                    <Typography sx={{ fontWeight: 'bold',textAlign: 'left',borderBottom: '1px solid #B0B0B0' }}>
                      General Sensor Simulation
                    </Typography>
                    <SimulationGauge />
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Utility function to round a number to two decimal places
function roundToTwoDecimalPlaces(value) {
  return Math.round(value * 100) / 100;
}

function SimulationGauge() {
  const [gaugeValue, setGaugeValue] = useState(1200);
  const [simulatedTemperature, setSimulatedTemperature] = useState(0);
  const theme = useSelector((state) => state.themedata.theme);
  const [isCalculating, setIsCalculating] = useState(false);

  const temparray = {
    'HBB R': 55,
    'HBB Y': 57,
    'HBB B': 54,
    'CT R': 61,
    'CT Y': 62,
    'CT B': 61
  };
  const I2 = 1200;

  const gaugeOptions = {
    series: [
      {
        type: 'gauge',
        detail: { show: true, formatter: '{value} A' },
        data: [{ value: gaugeValue, name: 'I (current)' }],
        min: 800,
        max: 1600,
        startAngle: 180,
        endAngle: 0,
        center: ['50%', '50%'],
        itemStyle: {
          color: '#DC272D',
        },
        axisLine: {
          lineStyle: {
            width: 18,
            color: [[(gaugeValue-800)/800, '#0076F6'], [1, '#C1C1C1']],
          },
        },
        axisLabel: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        pointer: {
          show:true,
                icon:'path://M2.9,0.7L2.9,0.7c1.4,0,2.6,1.2,2.6,2.6v115c0,1.4-1.2,2.6-2.6,2.6l0,0c-1.4,0-2.6-1.2-2.6-2.6V3.3C0.3,1.9,1.4,0.7,2.9,0.7z',
                length: '20%', // Length of the pointer
                width: 8, // Width of the pointer
                itemStyle: {
                  color: '#DC272D', // Color of the pointer
                  borderRadius:8
                },
                offsetCenter: [0, '-85%'],
        },
        title: {
          show: true,
          offsetCenter: [0, '-3%'],
          textStyle: {
            color: '#0076F6',
            fontSize: 'calc(20px * (window.innerWidth / 1920))',
            fontWeight: 700,
          },
          text: 'Simulated Temperature',
        },
        detail:{
          offsetCenter:['0%','-25%'],
          color:theme == 'dark'?'#FFFFFF':'#000000',
          formatter: function (gaugeValue) {
            return gaugeValue.toFixed(2);
          },
          rich: {
            value: {
              fontSize: "25px",
              fontWeight:  400,
              color: '#FFFFFF',
              padding: [0, 0, -20, 10]
            },
            unit: {
              fontSize: 18,
              color: '#FFFFFF',
              padding: [0, 0, -20, 10]
            }
          }
        },
      },
    ],
    tooltip: {
      backgroundColor: '#005C8E',
      borderColor: '#000000',
      formatter: '{a} <br/>{b} : {c} A',
      
    },
  };

  const handleSliderChange = (event, newValue) => {
    setIsCalculating(true);
    setGaugeValue(newValue);
    setTimeout(() => {
      setIsCalculating(false);
    }, 2000);
  };

  return (
    <div>
    <Box sx={{ position: 'relative' , height: '250px', overflow: 'hidden'}}>
        <ReactECharts 
          option={gaugeOptions} 
          style={{ height: '400px' }}
        />
      </Box>
      <Box sx={{ position: 'relative', paddingLeft: 2, paddingRight: 2 }}>
        <Slider
          value={gaugeValue}
          min={800}
          max={1600}
          step={50}
          onChange={handleSliderChange}
          aria-labelledby="continuous-slider"
        />
      </Box>
  <Box sx={{ display: 'flex',flexDirection:'column', justifyContent: 'space-between',textAlign:'left', marginTop: 1 }}>
  <Typography sx={{ fontWeight: 'bold', marginBottom: 1, borderBottom: '1px solid #B0B0B0' }}>
    Simulated Temperature Data:
  </Typography>
  </Box>
  <Box sx={{ backgroundColor: theme === 'dark' ? '#000000' : '#F5F5F5', marginTop: 0.5, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
    {['HBB R', 'HBB Y', 'HBB B', 'CT R', 'CT Y', 'CT B'].map((label, index) => {
      const Icurrent = gaugeValue; // Example current value
      const T2 = temparray[label];
      const simulatedTemperature = Math.pow(Icurrent / I2, 61 / 100) * T2;
      return (
        <Box
          key={index}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            padding: 1,
            border: '1px solid #505050',
            backgroundColor: theme === 'dark' ? '#000000' : '#F5F5F5'
          }}
        >
          <Typography variant="h6" sx={{ color: theme === 'dark' ? '#B0B0B0' : '#000000', marginBottom: 1, textAlign: 'left' }}>
            {label}
          </Typography>
          {isCalculating ? (
            <Typography variant="h6" sx={{ color: theme === 'dark' ? 'white' : 'black', textAlign: 'left' }}>
              Calculating...
            </Typography>
          ) : Icurrent === 1200 ? (
            <Typography variant="h6" sx={{ color: theme === 'dark' ? 'white' : 'black', textAlign: 'left' }}>
              {`${T2} °C`}
            </Typography>
          ) : (
            <Typography variant="h6" sx={{ color: theme === 'dark' ? 'white' : 'black', textAlign: 'left' }}>
              {`${(simulatedTemperature * 0.98).toFixed(1)} °C to ${(simulatedTemperature * 1.07).toFixed(1)} °C`}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
    </div>
  );
}

function CameraController({ focusTarget }) {
  const { camera, scene } = useThree();

  useEffect(() => {
    if (!focusTarget) return;

    const object = scene.getObjectByName(focusTarget);
    if (!object) return;

    const box = new THREE.Box3().setFromObject(object);
    const center = new THREE.Vector3();
    box.getCenter(center);

    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    const newZ = center.z + maxDim * 2;
    const newPos = new THREE.Vector3(center.x, center.y, newZ);

    // Smoothly transition the camera to the new position
    camera.position.lerp(newPos, 0.1);
    camera.lookAt(center);
    camera.updateProjectionMatrix();

    // Adjust the camera's zoom level based on the size of the object
    const distance = camera.position.distanceTo(center);
    const zoomFactor = 1.5; // Adjust this factor to control zoom level
    camera.zoom = Math.min(Math.max(1, 10 / distance * zoomFactor), 10);
    camera.updateProjectionMatrix();
  }, [focusTarget]);

  return null;
}

function ThreeDScene() {
  const cameraRef = useRef();
  const livedata = useSelector((state) => state.mainpagedata.livedata);
  const theme = useSelector((state) => state.themedata.theme);
  const [cursor, setCursor] = useState('default');
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [viewMode, setViewMode] = useState('color');
  const [color, setColor] = useState('#ffffff');
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [mappings, setMappings] = useState({});
  const [mode, setMode] = useState('view');
  const [componentNames, setComponentNames] = useState({});
  const [showBuildPanel, setShowBuildPanel] = useState(false);
  const [mappingName, setMappingName] = useState('');
  const [visibleLayers, setVisibleLayers] = useState(['default', 'layerA', 'layerB', 'layerC']);
  const [componentData, setComponentData] = useState({});
  const [Icurrent, setIcurrent] = useState(1000); // Define Icurrent here

  // In this refactor, we no longer override the GLB's material color. We'll keep
  // the data structure "componentColors" around if needed for future logic,
  // but it does not affect rendering the original model color from the GLB.
  const [componentColors, setComponentColors] = useState(
    Object.keys(componentData).reduce((acc, key) => {
      acc[key] = 'white'; // no longer used for direct coloring
      return acc;
    }, {})
  );

  // Add a new state to store the initial mappings
  const [initialMappings, setInitialMappings] = useState({});

  /**
   * Mouse events to change cursor style
   */
  const handleMouseDown = (event) => {
    if (event.button === 0) {
      setCursor('grab');
    } else if (event.button === 2) {
      setCursor('move');
    }
  };

  const handleMouseUp = () => {
    setCursor('default');
  };

  const handleMouseWheel = () => {
    setCursor('zoom-in');
    setTimeout(() => setCursor('default'), 500);
  };

  useEffect(() => {
    if (livedata.length) {
      let componentDataTagId = {
        _1f1: { 
          temperatureSensor1: { tagid: '3315110', data: '' }, 
          temperatureSensor2: { tagid: '3315111', data: '' }, 
          temperatureSensor3: { tagid: '3315112', data: '' }, 
          temperatureSensor4: { tagid: '3315113', data: '' }, 
          temperatureSensor5: { tagid: '3315114', data: '' },
          temperatureSensor6: { tagid: '3315115', data: '' },
          temperatureSensor7: { tagid: '3315116', data: '' },
          temperatureSensor8: { tagid: '3315117', data: '' },
        },
        _1f3: { 
          // New mappings
          frame: { tagid: '3830680', data: '' },
          iframe: { tagid: '3830681', data: '' },
          rating: { tagid: '3830682', data: '' },
          poles: { tagid: '3830683', data: '' },
          ir: { tagid: '3830684', data: '' },
          iy: { tagid: '3830685', data: '' },
          ib: { tagid: '3830686', data: '' },
          in: { tagid: '3830687', data: '' },
          ig: { tagid: '3830688', data: '' },
          vr: { tagid: '3830689', data: '' },
          vy: { tagid: '38306810', data: '' },
          vb: { tagid: '38306811', data: '' },
          vry: { tagid: '38306812', data: '' },
          vyb: { tagid: '38306813', data: '' },
          vbr: { tagid: '38306814', data: '' },
          totalActiveEnergy: { tagid: '38306815', data: '' },
          totalReactiveEnergy: { tagid: '38306816', data: '' },
          totalApparentEnergy: { tagid: '38306817', data: '' },
          contactWearR: { tagid: '38306818', data: '' },
          contactWearY: { tagid: '38306819', data: '' },
          contactWearB: { tagid: '38306820', data: '' },
          contactWearN: { tagid: '38306821', data: '' },
          incomerSideR: { tagid: '38306822', data: '' },
          incomerSideY: { tagid: '38306823', data: '' },
          incomerSideB: { tagid: '38306824', data: '' },
          incomerSideN: { tagid: '38306825', data: '' },
          loadSideR: { tagid: '38306826', data: '' },
          loadSideY: { tagid: '38306827', data: '' },
          loadSideB: { tagid: '38306828', data: '' },
          loadSideN: { tagid: '38306829', data: '' },
          faultCounterL: { tagid: '38306830', data: '' },
          faultCounterS: { tagid: '38306834', data: '' },
          faultCounterI: { tagid: '38306832', data: '' },
          faultCounterG: { tagid: '38306833', data: '' },
          faultCounterTripDueToETU: { tagid: '38306834', data: '' },
          onOperation: { tagid: '38306835', data: '' },
          offOperation: { tagid: '38306836', data: '' },
        },
        _2f2: {
          rmsVoltage1: { tagid: '9936590', data: '' },
          rmsVoltage2: { tagid: '9936591', data: '' },
          rmsVoltage3: { tagid: '9936592', data: '' },
          rmsCurrent1: { tagid: '9936593', data: '' },
          rmsCurrent2: { tagid: '9936594', data: '' },
          rmsCurrent3: { tagid: '9936595', data: '' },
          thdRPhaseCurrent: { tagid: '9936596', data: '' },
          thdYPhaseCurrent: { tagid: '9936597', data: '' },
          thdBPhaseCurrent: { tagid: '9936598', data: '' },
          thdRPhaseVoltage: { tagid: '9936599', data: '' },
          thdYPhaseVoltage: { tagid: '99365910', data: '' },
          thdBPhaseVoltage: { tagid: '99365911', data: '' },
        },
        _2f4: { vfdStatus: { tagid: '3691210', data: '' }, 
        motorVoltage: { tagid: '3691211', data: '0.00' }, 
        dcLinkVoltage: { tagid: '3691212', data: '' },
        current: { tagid: '3691213', data: '0.00' },
        power: { tagid: '3691214', data: '0.00' }, 
        frequency: { tagid: '3691215', data: '0.00' }, 
        powerFactor: { tagid: '3691216', data: '0.00' }, 
         },
      };

      for (let i = 0; i < livedata.length; i++) {
        const liveDataItem = livedata[i];
        //console/log("value card ", liveDataItem);


        // Check if the node exists in currentcomponentdata before accessing its properties
        Object.keys(componentDataTagId).forEach(node => {
          const nodeData = componentDataTagId[node];
          
          if (nodeData) {
            // Iterate over all properties in nodeData
            Object.keys(nodeData).forEach(property => {
              if (nodeData[property] && parseInt(nodeData[property].tagid) === parseInt(liveDataItem.TagId)) {
                nodeData[property].data = roundToTwoDecimalPlaces(parseFloat(liveDataItem.value));
                //console/log(`Updated ${property} for ${node}:`, nodeData[property].data);
              }
            });
          }
        });
      }

      //console/log("currentcomponentdata finally obtained", componentDataTagId);
      setComponentData(componentDataTagId);
    }
  }, [livedata]);

  useEffect(() => {
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('wheel', handleMouseWheel);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('wheel', handleMouseWheel);
    };
  }, []);

 

  const handleMappingChange = (component, dataName) => {
    //console/log(`Updating mapping for component: ${component} with dataName: ${dataName}`);
    setMappings((prevMappings) => ({
      ...prevMappings,
      [component]: dataName
    }));
  };


  useEffect(() => {
    //console/log('Mappings updated:', mappings);
  }, [mappings]);

  useEffect(() => {
    async function loadMappings() {
      try {
        const data = await fetchMappings();
        //console/log('Fetched mappings:', data);
        const mappings = data.reduce((acc, item) => {
          acc[item.component] = item.dataName;
          return acc;
        }, {});
        setMappings(mappings);
        setInitialMappings(mappings); // Save the initial mappings
      } catch (error) {
        console.error('Error fetching mappings:', error);
      }
    }

    loadMappings();
  }, []);

  const handleCreateMapping = async () => {
    //console/log('Creating mapping for selected components:', selectedComponents);
    // For data-layer mapping, unaffected by color in the 3D scene
    const newColors = { ...componentColors };
    const mappingsArray = selectedComponents.map((component) => {
      handleMappingChange(component, mappingName);
      newColors[component] = color; // This won't override the actual GLB color.
      return {
        component: component,
        dataName: mappingName,
        color: color
      };
    });
    setComponentColors(newColors);

    try {
      // Post each mapping as a JSON object to the database
      for (const mapping of mappingsArray) {
        await createMapping(mapping);
      }
      // Refresh mappings after creating new ones
      const updatedMappings = await fetchMappings();
      setMappings(updatedMappings.reduce((acc, item) => {
        acc[item.component] = item.dataName;
        return acc;
      }, {}));
    } catch (error) {
      console.error('Error creating mapping:', error);
    }

    setShowBuildPanel(false);
    setSelectedComponent(null);
    setSelectedComponents([]);
  };

  const handleRestoreDefaults = () => {
    setComponentColors(
      Object.keys(componentData).reduce((acc, key) => {
        acc[key] = 'white';
        return acc;
      }, {})
    );
    setMappings(initialMappings); // Restore to initial mappings
    setSelectedComponent(null);
    setSelectedComponents([]);
  };

  const handleSelectComponent = (component) => {
    //console/log("Selected component:", component);
    if (mode === 'view') {
      setSelectedComponents([component]);
      if (selectedComponents.length === 0) {
        handleViewClick();
      }
    } else if (mode === 'build') {
      setSelectedComponents((prevSelected) => {
        if (!prevSelected.includes(component)) {
          return [...prevSelected, component];
        }
        return prevSelected;
      });
    }
    setSelectedComponent(component);
  };

  // Use theme variable in your component
  const canvasBackgroundColor = theme === 'dark' ? '#101010' : '#ffffff';

  // example: user toggles a checkbox for a layer
  const handleToggleLayer = (layer) => {
    setVisibleLayers((prev) =>
      prev.includes(layer)
        ? prev.filter((l) => l !== layer)
        : [...prev, layer]
    );
  };

  // Call fetchData
  useEffect(() => {
    fetchData();
  }, []);


  // Function to handle the "View" button click
  const handleViewClick = async () => {
    setMode('view');
    setShowBuildPanel(false);
    await fetchMappings(); // Fetch mappings when "View" is clicked
  };

  async function handleRemoveAllMappings() {
    try {
      // Clear all mappings in the state
      setMappings({}); // Set mappings to an empty object
      
      //console/log('All mappings set to empty.');
    } catch (error) {
      console.error('Error setting mappings to empty:', error);
    }
  }

  const handleZoom = (direction) => {
    if (!cameraRef.current) return;
    const zoomFactor = direction === 'in' ? 0.9 : 1.1;
    cameraRef.current.position.multiplyScalar(zoomFactor);
    cameraRef.current.updateProjectionMatrix();
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '99%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: canvasBackgroundColor
      }}
    >
      <LoadingIndicator />
      <Box sx={{ flex: 1, position: 'relative', display: 'flex' }}>
        {showBuildPanel && mode === 'build' ? (
          <Draggable
            onStart={() => setCursor('grabbing')}
            onStop={() => setCursor('default')}
          >
            <Box
              sx={{
                position: 'absolute',
                left: '5%',
                top: '5%',
                transform: 'translate(-50%, -50%)',
                width: '300px',
                backgroundColor: "#303030",
                zIndex: 3,
                padding: 3,
                cursor: cursor,
                boxShadow: '0 0 10px 2px rgba(30, 144, 255, 0.8)',
                borderRadius: '2px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', marginBottom: 2 }}>
                Create Mapping
              </Typography>
              <TextField
                select
                label="Select Name"
                value={mappingName}
                onChange={(e) => setMappingName(e.target.value)}
                fullWidth
                sx={{ marginBottom: 2 }}
                InputLabelProps={{ style: { color: 'white' } }}
                InputProps={{ style: { color: 'white' } }}
              >
                {Object.keys(componentData).map((dataName) => (
                  <MenuItem key={dataName} value={dataName}>
                    {dataName}
                  </MenuItem>
                ))}
              </TextField>
              <SketchPicker
                color={color}
                onChangeComplete={(col) => setColor(col.hex)}
                sx={{ marginBottom: 2 }}
              />
              <Button variant="contained" color="primary" onClick={handleCreateMapping} fullWidth>
                Create
              </Button>
            </Box>
          </Draggable>
        ) : (
          selectedComponent && mode === 'view' && mappings[selectedComponent] && (
            <Box
              sx={{
                position: 'absolute',
                marginTop: '5px',
                left: 0,
                width: '30%',
                height: '98%',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                cursor: cursor
              }}
            >
              <ComponentDetails
                selectedComponent={selectedComponent}
                componentNames={componentNames}
                mappings={mappings}
                componentData={componentData}
                Icurrent={Icurrent} // Pass Icurrent as a prop
              />
            </Box>
          )
        )}

        <Canvas
          shadows={false}
          camera={{ fov: 45, position: [0, 0, 5] }}
          onCreated={({ camera }) => (cameraRef.current = camera)}
          style={{ width: '100%', height: '100%' }}
        >
          <color attach="background" args={[canvasBackgroundColor]} />
          <Stage intensity={0.08} environment={null}>
            <Model
              onSelect={handleSelectComponent}
              viewMode={viewMode}
              mode={mode}
              selectedComponent={selectedComponent}
              visibleLayers={visibleLayers}
              mappings={mappings}
            />
          </Stage>
          <CameraController focusTarget={selectedComponent} />
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            target={[0, 0, 0]}
            enableDamping={true}
            dampingFactor={0.2}
            rotateSpeed={0.5}
          />
        </Canvas>

        <div style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '50px',
          gap: '8px',
         
        }}>
          <Button onClick={() => handleZoom('in')} variant="contained"><ZoomInIcon /></Button>
          <Button onClick={() => handleZoom('out')} variant="contained"><ZoomOutIcon /></Button>
        </div>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          backgroundColor: 'transparent',
          gap: 1,
          padding: 1,
          width: '5%',
        }}
      >
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent',
          padding: 1,
        }}
      >
        <FormControl variant="standard" sx={{ marginTop: "0px", minWidth: 50 }}>
          <Select
            defaultValue={viewMode}
            labelId="demo-simple-select-standard-label"
            id="demo-simple-select-standard"
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              "&:before": {
                borderBottom: `1px solid ${theme === 'light' ? '#3C3C3C' : '#FFFFFF'}`,
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottom: '2px solid #008CD7',
              },
              "&:after": {
                borderBottom: '2px solid #008CD7',
              },
              margin: 0,
              padding: 0,
              width: '175px',
              fontSize: "18px",
              fontFamily: "Roboto",
              fontWeight: '700 !important',
              color: 'var(--PrimaryText)',
              '& .MuiInputBase-input': {
                paddingLeft: 0,
              },
              '& .MuiInputBase-input:focus': {
                color: 'var(--HighlightedText_Stroke)',
                borderBottom: '2px solid var(--HighlightedText_Stroke)',
                backgroundColor: 'transparent',
                fontWeight: '700 !important',
                '& .MuiSvgIcon-root': {
                  color: 'var(--HighlightedText_Stroke) !important',
                },
              },
              '& .MuiSvgIcon-root': {
                color: 'var(--PrimaryText)',
              },
              '& .MuiNativeSelect-select': {
                paddingTop: 0,
                paddingBottom: 0,
              },
              '& .MuiNativeSelect-select option': {
                backgroundColor: 'var(--dropdown-bg) !important',
                fontSize: "18px",
                fontFamily: "Roboto",
                fontWeight: 400,
                color: 'var(--PrimaryText) !important',
              },
              '& .MuiNativeSelect-select option:checked': {
                backgroundColor: 'var(--dropdown-bg) !important',
                color: 'var(--White) !important',
                fontWeight: '700 !important',
              },
              '& .MuiInputBase-input option:hover': {
                backgroundColor: '#005D8F !important',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: "100px",
                  bgcolor: theme === 'dark' ? '#3C3C3C' : '#C1C1C1',
                  borderBottom: '2px solid #008CD7',
                  borderRadius: 0,
                  boxShadow: 'none',
                  maxHeight: '250px',
                  overflow: 'auto',
                  color: theme === 'dark' ? '#FFFFFF' : '#3C3C3C',
                },
              },
            }}
            label=""
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
          >
            <MenuItem sx={{ paddingLeft: 1 }} value="color">Solid</MenuItem>
            <MenuItem sx={{ paddingLeft: 1 }} value="translucent">Translucent</MenuItem>
            <MenuItem sx={{ paddingLeft: 1 }} value="wireframe">Wireframe</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

export default ThreeDScene;